/**
 * Manual Sync Controls Components
 * 
 * Components for manual sync operations, retry functionality, and user feedback
 */

import { RefreshCw, Upload, AlertTriangle, WifiOff } from 'lucide-react';
import { useSyncStatus, useNetworkStatus } from '@/contexts/offline';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';

interface ManualSyncControlsProps {
  className?: string;
  variant?: 'button' | 'card' | 'inline';
  showFullSync?: boolean;
  onSyncComplete?: () => void;
}

/**
 * Manual Sync Controls Component
 */
export function ManualSyncControls({
  className,
  variant = 'button',
  showFullSync = true,
  onSyncComplete
}: ManualSyncControlsProps) {
  const { isSyncing, syncError, lastSyncTime, performSync } = useSyncStatus();
  const { isOnline } = useNetworkStatus();
  const { toast } = useToast();
  const [isManualSyncing, setIsManualSyncing] = useState(false);

  const handleFullSync = async () => {
    if (!isOnline) {
      toast({
        title: 'Cannot sync',
        description: 'You are currently offline. Sync will resume when connection is restored.',
        variant: 'destructive'
      });
      return;
    }

    setIsManualSyncing(true);
    try {
      const success = await performSync();
      if (success) {
        toast({
          title: 'Sync completed',
          description: 'Your data has been synchronized successfully.',
          variant: 'default'
        });
        onSyncComplete?.();
      } else {
        toast({
          title: 'Sync failed',
          description: 'Failed to sync data. Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Manual sync failed:', error);
      toast({
        title: 'Sync failed',
        description: 'Failed to sync data. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsManualSyncing(false);
    }
  };



  const formatLastSyncTime = () => {
    if (!lastSyncTime) return 'Never';
    
    try {
      const date = new Date(lastSyncTime);
      return date.toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  if (variant === 'button') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        {showFullSync && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleFullSync}
            disabled={isSyncing || isManualSyncing || !isOnline}
            className="gap-2"
          >
            <RefreshCw className={cn('h-4 w-4', (isSyncing || isManualSyncing) && 'animate-spin')} />
            {isSyncing || isManualSyncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        )}
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center justify-between p-3 bg-gray-50 rounded-lg border', className)}>
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-600">
            Last sync: {formatLastSyncTime()}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {showFullSync && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleFullSync}
              disabled={isSyncing || isManualSyncing || !isOnline}
              className="h-8 px-2"
            >
              <RefreshCw className={cn('h-3 w-3', (isSyncing || isManualSyncing) && 'animate-spin')} />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Card variant
  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Sync Controls
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {syncError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Sync error: {syncError}
            </AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-2">
          <div className="text-sm text-gray-600">
            Last sync: {formatLastSyncTime()}
          </div>
          
          {!isOnline && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                You are offline. Sync operations will resume when connection is restored.
              </AlertDescription>
            </Alert>
          )}
        </div>
        
        <div className="flex flex-col gap-2">
          {showFullSync && (
            <Button
              onClick={handleFullSync}
              disabled={isSyncing || isManualSyncing || !isOnline}
              className="w-full gap-2"
            >
              <Upload className={cn('h-4 w-4', (isSyncing || isManualSyncing) && 'animate-spin')} />
              {isSyncing || isManualSyncing ? 'Syncing Changes...' : 'Sync All Changes'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Quick Sync Button
 * Simple button for quick sync operations
 */
export function QuickSyncButton({ 
  className,
  size = 'sm'
}: { 
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const { performSync } = useSyncStatus();
  const { isSyncing } = useSyncStatus();
  const { isOnline } = useNetworkStatus();
  const { toast } = useToast();
  const [isManualSyncing, setIsManualSyncing] = useState(false);

  const handleSync = async () => {
    if (!isOnline) {
      toast({
        title: 'Cannot sync',
        description: 'You are currently offline.',
        variant: 'destructive'
      });
      return;
    }

    setIsManualSyncing(true);
    try {
      const success = await performSync();
      if (success) {
        toast({
          title: 'Synced',
          description: 'Data synchronized successfully.',
          variant: 'default'
        });
      } else {
        toast({
          title: 'Sync failed',
          description: 'Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Sync failed',
        description: 'Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsManualSyncing(false);
    }
  };

  return (
    <Button
      variant="outline"
      size={size}
      onClick={handleSync}
      disabled={isSyncing || isManualSyncing || !isOnline}
      className={cn('gap-2', className)}
    >
      <RefreshCw className={cn(
        size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4',
        (isSyncing || isManualSyncing) && 'animate-spin'
      )} />
      {isSyncing || isManualSyncing ? 'Syncing' : 'Sync'}
    </Button>
  );
}

/**
 * Retry Sync Button
 * Specifically for retrying failed sync operations
 */
export function RetrySyncButton({ className }: { className?: string }) {
  const { syncError } = useSyncStatus();

  if (!syncError) {
    return null;
  }

  return (
    <QuickSyncButton
      className={cn('text-red-600 border-red-200 hover:bg-red-50', className)}
    />
  );
}

/**
 * Offline Alert Sync Button
 * Shows offline alert when offline, sync functionality when online
 */
export function OfflineAlertSyncButton({
  className,
  size = 'sm'
}: {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const { performSync } = useSyncStatus();
  const { isSyncing } = useSyncStatus();
  const { isOnline } = useNetworkStatus();
  const { toast } = useToast();
  const [isManualSyncing, setIsManualSyncing] = useState(false);

  const handleClick = async () => {
    if (!isOnline) {
      toast({
        title: 'You are offline',
        description: 'Sync will resume automatically when your connection is restored.',
        variant: 'default'
      });
      return;
    }

    // If online, perform sync
    if (isSyncing || isManualSyncing) return;

    setIsManualSyncing(true);

    // Set up a timeout fallback to ensure the button stops spinning
    const timeoutId = setTimeout(() => {
      setIsManualSyncing(false);
    }, 10000); // 10 second timeout

    try {
      await performSync();
      toast({
        title: 'Sync completed',
        description: 'Your data has been synchronized successfully.',
        variant: 'default'
      });
    } catch (error) {
      console.error('Manual sync failed:', error);
      toast({
        title: 'Sync failed',
        description: 'Failed to sync data. Will retry automatically.',
        variant: 'destructive'
      });
    } finally {
      clearTimeout(timeoutId);
      setIsManualSyncing(false);
    }
  };

  return (
    <Button
      variant="outline"
      size={size}
      onClick={handleClick}
      className={cn('gap-2', className)}
    >
      {!isOnline ? (
        <>
          <WifiOff className={cn(
            size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'
          )} />
          Offline
        </>
      ) : (
        <>
          <RefreshCw className={cn(
            size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4',
            (isSyncing || isManualSyncing) && 'animate-spin'
          )} />
          {isSyncing || isManualSyncing ? 'Syncing' : 'Sync'}
        </>
      )}
    </Button>
  );
}
